import { SmileOutlined } from '@ant-design/icons';
import { Flex, Segmented, theme, Input, Typography, Tooltip, Button } from 'antd';
import React, { useMemo, useState } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import { EMOJI_DATA, EMOJI_MAP } from './data';
import './styles.css';

const EMOJI = 'emoji';
const { useToken } = theme;
const { Search } = Input;
const { Text } = Typography;

const EMOJIS_PER_ROW = 11; // emojis per grid row
const EMOJI_SIZE = 40; // width and height of emoji cell in px

const getAllSearchEmoji = (searchTerm) => {
  const filteredObj = {};
  for (const [key, value] of Object.entries(EMOJI_DATA)) {
    const normalizedSearchTerm = searchTerm.toLowerCase().split(' ').join('_');
    const searchEmojis = value.items.filter((item) => item.toLowerCase().includes(normalizedSearchTerm));
    if (searchEmojis?.length > 0) {
      if (filteredObj[key] === undefined) {
        filteredObj[key] = { ...value, items: [] };
      }
      filteredObj[key].items = searchEmojis;
    }
  }
  return filteredObj;
};

export const EmojiPicker = React.memo(({ onClick, onClose, showRemove = true }) => {
  const { token } = useToken();
  const [activeSegment, setActiveSegment] = useState(EMOJI);
  const [searchTerm, setSearchTerm] = useState('');

  const options = useMemo(() => [{ label: 'Emoji', value: EMOJI, icon: <SmileOutlined /> }], []);

  const filteredItems = useMemo(() => {
    return searchTerm ? getAllSearchEmoji(searchTerm) : EMOJI_DATA;
  }, [searchTerm]);

  return (
    <Flex
      gap={token.marginXS}
      style={{ padding: 0, paddingTop: token.paddingSM, paddingBottom: token.paddingSM, overflow: 'auto' }}
      vertical
      className="emoji-picker"
    >
      <Flex
        justify="space-between"
        align="flex-start"
        style={{ padding: token.paddingLG - 4, paddingTop: 0, paddingBottom: 0 }}
      >
        <Segmented options={options} value={activeSegment} onChange={setActiveSegment} />
        {showRemove && (
          <Button onClick={onClose} size="small" type="text">
            Remove
          </Button>
        )}
      </Flex>
      <Flex style={{ padding: token.paddingLG - 4, paddingTop: 0, paddingBottom: 0 }}>
        <Search
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
          size="large"
          styles={{ input: { height: '40px' } }}
        />
      </Flex>

      <div style={{ overflowY: 'auto', maxHeight: 198, padding: token.paddingLG - 4, paddingTop: 0, paddingBottom: 0 }}>
        {Object.entries(filteredItems).map(([groupKey, group]) => {
          const emojiCount = group.items.length;
          const rowCount = Math.ceil(emojiCount / EMOJIS_PER_ROW);

          return (
            <Flex vertical className="emoji-group" key={groupKey} style={{ marginBottom: 16 }}>
              {/* Group Header full width */}
              <Flex className="emoji-group-header">
                <Text ellipsis className="emoji-group-header-text">
                  {group?.label}
                </Text>
              </Flex>

              {/* Virtualized emoji grid for this group */}
              <Grid
                columnCount={EMOJIS_PER_ROW}
                columnWidth={EMOJI_SIZE}
                height={rowCount * EMOJI_SIZE} // max height for group grid
                rowCount={rowCount}
                rowHeight={EMOJI_SIZE}
                width={EMOJIS_PER_ROW * EMOJI_SIZE}
              >
                {({ columnIndex, rowIndex, style }) => {
                  const emojiIndex = rowIndex * EMOJIS_PER_ROW + columnIndex;
                  if (emojiIndex >= emojiCount) return null;

                  const emojiKey = group.items[emojiIndex];
                  const emoji = EMOJI_MAP[emojiKey];

                  return (
                    <div
                      key={emojiKey}
                      style={{
                        ...style,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      className="emoji-item"
                      onClick={() => onClick?.({ emoji, code: emojiKey })}
                    >
                      <Tooltip title={emojiKey.replace(/_/g, ' ')}>{emoji}</Tooltip>
                    </div>
                  );
                }}
              </Grid>
            </Flex>
          );
        })}
      </div>
    </Flex>
  );
});

export default EmojiPicker;
